<script>
    import dressing from "@intellectability/platform-common";
    import {ACTIONS} from "@intellectability/platform-common/constants";
    import {onMount} from "svelte";
    import "https://early.webawesome.com/webawesome@3.0.0-alpha.13/dist/components/split-panel/split-panel.js";

    // common Svelte components
    import {
        Button,
        Icon,
        Masonry,
        Modal,
        userSecurity
    } from "@intellectability/platform-common/sv";
    import {actionCheck, create_security_error} from "@intellectability/platform-common/utils";
    import {Col, Collapse, ModalBody, ModalFooter, Row} from "@sveltestrap/sveltestrap";

    import {on} from "svelte/events";

    import {fade, fly} from "svelte/transition";
    import DashboardCard from "./lib/DashboardCard.svelte";

    let hasAccess = $derived(actionCheck(userSecurity.userActions));

    const {DASHBOARD_VIEW, DASHBOARD_MANAGE} = ACTIONS;

    let allowManageDashboard = $derived(hasAccess(DASHBOARD_MANAGE));

    let loading = $state(true);

    let allDashboardCards = $state.raw([]);

    $inspect(userSecurity);

    // cards to render
    let dashboardCards = $derived.by(function () {

        if (allDashboardCards.length === 0) {
            return [];
        }

        return allDashboardCards
            .filter(card => {
                return card.card_mandatory === true || card.display === true;
            })
            .toSorted((a, b) => {
                return a.sort_order - b.sort_order;
            });

    });

    let smallDashboardCards = $derived(dashboardCards
        .filter(e => e.card_size === "Small" && e.card_type !== "DataTable"));

    const HIGHLIGHT_CLUSTER_SIZE = 4; // number of cards to highlight

    let highlightClusterCards = $derived(smallDashboardCards.slice(0, HIGHLIGHT_CLUSTER_SIZE));
    let remainingSmall = $derived(smallDashboardCards.slice(HIGHLIGHT_CLUSTER_SIZE));

    // let's section off tabular and otherwise large cards
    let largeDashboardCards = $derived(dashboardCards
        .filter(e => e.card_size === "Pagewide" || e.card_type === "DataTable")
    );

    let refreshMasonryLayout = $state(null); // bound function to refresh as each card loads

    let showSettingsModal = $state(false);

    onMount(function load() {

        if (userSecurity.loggedIn && !hasAccess(DASHBOARD_VIEW)) {
            const err = create_security_error("Dashboard.svelte", "Dashboard", DASHBOARD_VIEW);
            dressing.api.report_security_error(err).then(() => dressing.api.logout());
        }
        else {
            fetch_cards();

        }
    });

    function fetch_cards() {
        loading = true;
        dressing.get("/dashboard/cards")
            .then(({data: response}) => {

                const {success, data} = response;

                if (success === true) {
                    allDashboardCards = data.cards;
                }
                loading = false;
            });
    }

    let showRemainingSmallCards = $state(false);

    // generate a random delay for each card to animate in, purely for visual interest
    function generateRandomDelay() {
        const MAXIMUM_DELAY = 500;
        const random = Math.floor(Math.random() * 1000);
        return Math.min(random, MAXIMUM_DELAY);
    }

    let remainingCardsSectionRef = $state(); // use to access elem within shadow dom

    let dashboardManagementError = $state(false);
    let dashboardManagementErrorText = $state("");

    function toggleCardVisibility(event) {
        event.stopImmediatePropagation();

        dashboardManagementError = false;

        let t = event.target;
        let card_id = t.value;
        let checked = t.checked;

        dressing.post("dashboard/cards/toggle", {
            card_id,
            display: checked
        }).then(({data: res}) => {
            if (res.success !== true) {
                dashboardManagementError = true;
                dashboardManagementErrorText = res.message || "Could not toggle card visibility";
            }
            else {
                fetch_cards();
            }
        }).catch(() => {
            dashboardManagementError = true;
            dashboardManagementErrorText = "Could not toggle card visibility";
        });
    }

    function moveCard(card_id, direction) {
        dashboardManagementError = false;
        dressing.post("dashboard/cards/move_card", {
            card_id,
            direction
        }).then(({data: res}) => {
            if (res.success !== true) {
                dashboardManagementError = true;
                dashboardManagementErrorText = res.message || "Could not move card";
            }
            else {
                fetch_cards();
            }
        }).catch(() => {
            dashboardManagementError = true;
            dashboardManagementErrorText = "Could not move card";
        });
    }

    let splitContainerRef = $state(null);
    let highlightClusterCardsRef = $state(null);

    let offSelf = $state(null);

    let tableSectionHeight = $state(465);

    $effect(() => {
        if (splitContainerRef && !loading) {
            offSelf = on(splitContainerRef, "wa-reposition", e => {
                tableSectionHeight = calculate_highlight_cluster_cards();
            });

            return function () {
                offSelf?.();
            };
        }
    });

    function calculate_highlight_cluster_cards() {
        if (highlightClusterCardsRef) {
            const h = highlightClusterCardsRef.getBoundingClientRect().height;
            return h < 150
                ? 465
                : h;

        }
        return 465;
    }

</script>

<div>
    <link rel="stylesheet" href="https://early.webawesome.com/webawesome@3.0.0-alpha.13/dist/styles/utilities.css"/>
    <!-- Page Title -->
    <Row class="align-items-center mb-3">
        <Col>
            <h1>
                Dashboard
            </h1>
        </Col>
        {#if allowManageDashboard}
            <Col xs="auto">
                <div>
                    <Button onclick={() => showSettingsModal = true} appearance="filled">
                        <Icon icon="gears" class="me-lg-2" variant="solid"/>
                        <span class="d-none d-lg-inline">
                                    Dashboard Settings
                                </span>
                    </Button>
                </div>
            </Col>
        {/if}
    </Row>

    <!-- Highlight row -->
    <div>
        <div style="margin:0; box-sizing: border-box;" class="d-lg-none">
            <Row class="mb-3 ">
                <div class={(highlightClusterCards.length > 0 ? "col-xl-7 col-12" : "col-12")}>
                    <div class="border bg-light p-4 rounded position-relative h-100">
                        {#if !loading}
                            <div class="section-title">
                                Highlighted Cards
                            </div>
                        {/if}
                        <div>
                            {#if loading}
                                <div class="d-grid"
                                     style="grid-template-columns: 1fr 1fr; grid-gap: 0.75rem;">
                                    {#each {length: 4} as k, i}
                                        {@render loadingCard(i)}
                                    {/each}
                                </div>
                            {:else}
                                {#if highlightClusterCards.length > 0}
                                    <Row class="g-3 align-item-center">
                                        {#each highlightClusterCards as cardData, i}
                                            {@const delay = i > 0 ? generateRandomDelay() : 0}
                                            <Col xs="12" lg="3" xl="6">
                                                <DashboardCard highlight_rank={i + 1}
                                                               fixedHeight={200}
                                                               {cardData}
                                                               onloaded={refreshMasonryLayout}
                                                               delayAnimationMS={delay}
                                                />
                                            </Col>
                                        {/each}
                                    </Row>
                                {:else}
                                    <div class="d-flex my-4 bg-primary-100 rounded"
                                         transition:fade|global style="height: 200px;">
                                        <div class="m-auto text-primary-700 fw-bold">
                                            <Icon icon="face-monocle" variant="regular"
                                                  class="me-2 align-middle"
                                                  style="font-size: 32px;"/>
                                            We don't have any cards to show you. Turn some on in Dashboard
                                            Settings!
                                        </div>
                                    </div>
                                {/if}
                            {/if}
                        </div>
                    </div>
                </div>
                <div class={"px-0 " + (largeDashboardCards.length > 0 ? "col-xl-5 col-12" : "col-12")}>
                    <div class="px-2 overflow-hidden">
                        {#if !loading}
                            <wa-tab-group noScrollControls={false}>
                                {#each largeDashboardCards as cardData, i}
                                    <wa-tab panel={cardData.id} active={i === 0}>{cardData.card_name}</wa-tab>
                                    <wa-tab-panel name={cardData.id} active={i === 0}>
                                        <DashboardCard {cardData}/>
                                    </wa-tab-panel>
                                {/each}
                            </wa-tab-group>
                        {/if}
                    </div>
                </div>
            </Row>
        </div>
        <div class="d-none d-lg-block mb-3">
            <wa-split-panel primary="end" bind:this={splitContainerRef} style="">
                <wa-icon slot="divider" name="grip-vertical" variant="solid"></wa-icon>
                <div slot="start" style="align-self: flex-start">
                    <div class="border bg-light p-4 rounded position-relative h-100 mx-2"
                         bind:this={highlightClusterCardsRef}>
                        {#if !loading}
                            <div class="section-title">
                                Highlighted Cards
                            </div>
                        {/if}
                        <div>
                            {#if loading}
                                <div class="d-grid"
                                     style="grid-template-columns: 1fr 1fr; grid-gap: 0.75rem;">
                                    {#each {length: 4} as k, i}
                                        {@render loadingCard(i)}
                                    {/each}
                                </div>
                            {:else}
                                {#if highlightClusterCards.length > 0}
                                    <div class="wa-grid">
                                        {#each highlightClusterCards as cardData, i}
                                            {@const delay = i > 0 ? generateRandomDelay() : 0}
                                            <div>
                                                <DashboardCard highlight_rank={i + 1}
                                                               fixedHeight={200}
                                                               {cardData}
                                                               onloaded={refreshMasonryLayout}
                                                               delayAnimationMS={delay}
                                                />
                                            </div>
                                        {/each}

                                    </div>
                                {:else}
                                    <div class="d-flex my-4 bg-primary-100 rounded"
                                         transition:fade|global style="height: 200px;">
                                        <div class="m-auto text-primary-700 fw-bold">
                                            <Icon icon="face-monocle" variant="regular"
                                                  class="me-2 align-middle"
                                                  style="font-size: 32px;"/>
                                            We don't have any cards to show you. Turn some on in Dashboard
                                            Settings!
                                        </div>
                                    </div>
                                {/if}
                            {/if}
                        </div>
                    </div>
                </div>

                <div slot="end">
                    <div class="px-2 overflow-hidden" style="height: {tableSectionHeight || 350}px">
                        {#if !loading}
                            <wa-tab-group noScrollControls={false} class="h-100">
                                {#each largeDashboardCards as cardData, i}
                                    <wa-tab panel={cardData.id} active={i === 0}>{cardData.card_name}</wa-tab>
                                    <wa-tab-panel name={cardData.id} active={i === 0}>
                                        <DashboardCard fixedHeight={tableSectionHeight} {cardData}/>
                                    </wa-tab-panel>
                                {/each}
                            </wa-tab-group>
                        {/if}
                    </div>
                </div>
            </wa-split-panel>
        </div>
    </div>

    <Row>
        {#if remainingSmall.length > 0}
            <div class="mb-3">
                <Button class="w-100 d-block" variant="neutral" appearance="outlined filled"
                        onclick={() => showRemainingSmallCards = !showRemainingSmallCards}>
                    {#if showRemainingSmallCards}
                        <div>
                            <Icon icon="chevron-up" fixedWidth class="me-2" variant="regular"/>
                            Hide Rest
                            <Icon icon="chevron-up" fixedWidth class="ms-2" variant="regular"/>
                        </div>
                    {:else}
                        <div>
                            <Icon icon="chevron-down" fixedWidth class="ms-2" variant="regular"/>
                            Show Rest
                            <Icon icon="chevron-down" fixedWidth class="ms-2" variant="regular"/>
                        </div>
                    {/if}
                </Button>
            </div>

            <Collapse isOpen={showRemainingSmallCards} on:opening={function() {

                        const clientRect = remainingCardsSectionRef.getBoundingClientRect();
                        let offset = clientRect.top;

                        // find offset based on element top minus menu height
                        const menu_bar = document.querySelector(".top-bar.sticky");

                        if (menu_bar) {
                            const menu_bar_rect = menu_bar.getBoundingClientRect();
                            offset = offset - (menu_bar_rect.height + 10);
                        }

                        window.scrollBy({
                            top: offset,
                            behavior: "smooth"
                        });
                    }}>
                <div bind:this={remainingCardsSectionRef}
                     style="scroll-margin-top: 200px; scroll-padding-top: 200px;"
                     id="remaining-cards-section" class="border bg-primary-50 p-2 rounded">
                    <div class="transition-container">
                        {#if loading}
                            <div class="d-grid"
                                 style="grid-template-columns: 1fr 1fr 1fr; grid-gap: 0.75rem;">
                                {#each {length: 21} as k, i}
                                    {@render loadingCard(i)}
                                {/each}
                            </div>
                        {:else}
                            {#if remainingSmall.length > 0}
                                <Masonry padding="5px 15px"
                                         gridGap={'0.75rem'}
                                         bind:refreshLayout={refreshMasonryLayout}>
                                    {#each remainingSmall as cardData, i}
                                        {@const delay = i * 75}
                                        <DashboardCard
                                                {cardData}
                                                onloaded={refreshMasonryLayout}
                                                delayAnimationMS={delay}
                                        />
                                    {/each}
                                </Masonry>
                            {:else}
                                <div class="d-flex my-4 bg-primary-100 rounded"
                                     transition:fade|global style="height: 200px;">
                                    <div class="m-auto text-primary-700 fw-bold">
                                        <Icon icon="face-monocle" variant="regular"
                                              class="me-2 align-middle"
                                              style="font-size: 32px;"/>
                                        We don't have any cards to show you. Turn some on in Dashboard
                                        Settings!
                                    </div>
                                </div>
                            {/if}
                        {/if}
                    </div>
                </div>
            </Collapse>
        {/if}
    </Row>
</div>

{#if allowManageDashboard}
    <Modal bind:showModal={showSettingsModal} size="xl" title="Dashboard Settings">
        <ModalBody>
            <wa-callout variant="neutral" appearance="outlined filled" class="mb-4 mx-4">
                <div slot="icon" style="font-size: 32px;">
                    <Icon icon="gauge-circle-plus" variant="regular"/>
                </div>
                You can choose items to display below. The first four cards will appear in the <i>Highlighted
                Cards</i> section.
                Note: some items are <strong>mandatory</strong> and cannot be turned off.
            </wa-callout>
            {#if dashboardManagementError}
                <div in:fade class="position-fixed top-0 mt-3 translate-middle-x start-50">
                    <wa-callout variant="danger" class="shadow mx-4 p-4 pe-5">
                        <wa-icon slot="icon" name="circle-exclamation" variant="regular"></wa-icon>
                        <div class="mx-3">
                            {dashboardManagementErrorText}
                        </div>
                        <div class="position-absolute top-0 end-0">
                            <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
                            <wa-icon-button name="xmark-large" class="text-danger-600"
                                            style="--background-color-hover: var(--hrst-red-100)"
                                            onclick={() => dashboardManagementError = false}></wa-icon-button>
                        </div>
                    </wa-callout>
                </div>
            {/if}


            {#each allDashboardCards as card, i}
                {@const cardCount = allDashboardCards.length}
                <div class="mb-3">
                    {@render dashboardSetting(card, i, cardCount)}
                </div>
            {/each}
        </ModalBody>
        <ModalFooter class="justify-content-end">
            <button class="btn btn-primary rounded-pill btn-lg fs-6"
                    onclick={() => showSettingsModal = false}>
                Close
            </button>
        </ModalFooter>
    </Modal>
{/if}

{#snippet dashboardSetting({display, id, card_name, card_description, card_mandatory}, i, cardCount)}
    {@const isMandatory = card_mandatory}
    <div>
        <wa-details appearance="outlined">
                                        <span slot="summary" class="w-100">
                                            {@render settingRow(id, card_name, isMandatory, display, i, cardCount)}
                                        </span>
            <div slot="expand-icon">
                <Icon icon="square-plus" variant="light"/>
            </div>
            <div slot="collapse-icon">
                <Icon icon="square-minus" variant="regular"/>
            </div>
            <div>
                <h4>Description</h4>
                {card_description}
            </div>
        </wa-details>
    </div>
{/snippet}

{#snippet settingRow(id, title, isMandatory, enabled, index, cardCount)}
    {@const keyname = id}
    <Row class="align-items-center"
         onmouseover={(e) => {
                                        // Find the parent wa-details element
                                        const details = e.currentTarget.closest('wa-details');

                                        // @ts-ignore Check if the target is not an interactive element
                                        try {
                                            if (!e.target?.closest('wa-switch') && !e.target.closest('wa-button') && !e.target.closest('a')) {
                                                details?.classList.add('ready-to-open');
                                            }
                                        } catch (e) {}
                                     }}
         onmouseout={(e) => {
                                        // Find the parent wa-details element
                                        const details = e.currentTarget.closest('wa-details');
                                        details?.classList.remove('ready-to-open');
                                     }}
    >
        <Col xs="12" lg="" class="mb-3 mb-lg-0">
            <div class="fw-bold">{title}</div>
        </Col>
        <Col xs="6" lg="3" class="pe-0">
            <div style="width: fit-content" class="me-3">
                <div class="form-text fw-bold text-center mb-2 d-lg-none">Toggle display</div>
                {#if isMandatory}
                    <div class="d-flex align-items-center fw-bold">
                        <wa-switch size="small" defaultChecked={true} disabled>
                            Mandatory
                        </wa-switch>
                    </div>
                {:else}
                    <a onclick={function(e) {
                                                e.stopImmediatePropagation();
                                            }} class="fw-bold" onkeydown={() => {}} role="switch" href="."
                       aria-checked={enabled}>
                        <wa-switch id={"toggle-" + keyname} size="small"
                                   defaultChecked={enabled}
                                   value={keyname}
                                   onchange={toggleCardVisibility}>
                            {enabled ? "Shown" : "Hidden"}
                        </wa-switch>
                        <wa-tooltip for={"toggle-" + keyname} placement="bottom">
                            tacos
                        </wa-tooltip>
                    </a>
                {/if}
            </div>
        </Col>
        <Col xs="auto" class="ps-0">
            <div style="width: fit-content" class="me-3">
                <div class="d-lg-none form-text text-center fw-bold mb-2 d-lg-none">Reorder</div>
                <wa-button-group size="small">
                    <Button id={"dashboard-setting-move-up__" + keyname} appearance="outlined" pill
                            onclick={() => moveCard(keyname, "up")} disabled={index === 0}>
                        <Icon icon="arrow-up" class="pe-none px-3" variant="solid"/>
                    </Button>
                    <Button id={"dashboard-setting-move-down" + keyname} appearance="outlined" pill
                            onclick={() => moveCard(keyname, "down")} disabled={index + 1 === cardCount}>
                        <Icon icon="arrow-down" class="pe-none px-3" variant="solid"/>
                    </Button>
                </wa-button-group>
                <wa-tooltip for={"dashboard-setting-move-up__" + keyname}
                            showDelay={350}
                            placement="top-end">Move card up in list
                </wa-tooltip>
                <wa-tooltip for={"dashboard-setting-move-down"  + keyname}
                            showDelay={350}
                            placement="bottom-start">Move card down in list
                </wa-tooltip>
            </div>
        </Col>
    </Row>
{/snippet}

{#snippet loadingCard(index)}
    <div in:fade|global={{duration: 350, delay: 50 * index}}
         out:fade|global={{duration:100}}>
        <div in:fly|global={{y: 20, duration: 350, delay: 50 * index}}>
            <wa-skeleton effect="sheen"></wa-skeleton>
        </div>
    </div>
{/snippet}

<style>
    @media only screen and (max-width: 1200px) {
        .my-little-grid > * {
            grid-column: 1 / 3;
        }
    }

    .section-title {
        position: absolute;
        top: 0;
        left: 10px;
        animation: huntUp 0.5s ease-in-out forwards;
        font-size: 16px;
        font-weight: bold;
        backdrop-filter: blur(10px);
        color: var(--hrst-secondary-700);
        z-index: 1;
    }

    @keyframes huntUp {
        0% {
            transform: translateY(0);
        }
        100% {
            transform: translateY(-50%);
        }
    }

    .transition-container {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: 1fr;
    }

    .transition-container > * {
        grid-row: 1;
        grid-column: 1;
    }

    wa-details {
        transition: box-shadow 0.15s linear;
    }

    wa-tab-group {
        --indicator-color: var(--hrst-blue-400);
        --track-color: var(--hrst-secondary-300);
        --track-width: 2px;
    }

    wa-tab-panel::part(base) {
        padding: 0;
    }

    wa-tab {
        --active-tab-color: var(--hrst-blue-700);
        font-size: 14px;
        font-weight: bold;
    }

    :global(wa-details:not([open]).ready-to-open) {
        box-shadow: 0 4px 0 0 #F4F3F4;
    }


    wa-skeleton {
        --color: var(--hrst-blue-100);
        --sheen-color: var(--hrst-warning-100);
        --border-radius: 7px;
        height: 165px;
    }
</style>